import searchIcon from "@layouts/betto/assets/search.webp";
import providersIcon from "@layouts/betto/assets/providersIcon.svg";
import { AuthCloseIcon } from "@layouts/betto/authPopup/icons/AuthCloseIcon.jsx";
import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { Translate } from "react-localize-redux";

import { BettoProviderLogo } from "./BettoProviderLogo";
import {
	BettoProviderCard,
	BettoProviderCardFooter,
	BettoProviderClearButton,
	BettoProviderCloseButton,
	BettoProviderCount,
	BettoProviderGameCount,
	BettoProviderGrid,
	BettoProviderHeader,
	BettoProviderInfo,
	BettoProviderName,
	BettoProviderOverlay,
	BettoProviderPanel,
	BettoProviderRoot,
	BettoProviderSearchBar,
	BettoProviderSearchIcon,
	BettoProviderSearchInput,
	BettoProviderSearchSection,
	BettoProviderTitle,
	BettoProviderTitleGroup,
	BettoProviderTrigger,
	BettoProviderTriggerClear,
	BettoProviderTriggerGroup,
	BettoProviderTriggerIcon,
} from "./elements";
import useController from "./useController";
import { getProviderLabel, getProviderTriggerLabel } from "./utils";

const BettoProviderModal = ({
	items,
	value,
	placeholder,
	onSelect,
	onClose,
	hideTrigger = false,
	listenForOpenEvent = false,
	onEventOpen,
	"data-testid": dataTestId,
}) => {
	const {
		refs: { searchInputRef },
		state: {
			open,
			searchTerm,
			isSearchFocused,
			filteredItems,
			isProviderActive,
			selectedKey,
			totalCount,
		},
		handlers: {
			handleOpen,
			handleClose,
			handleSelect,
			handleClearSelection,
			handleClearSearch,
			setSearchTerm,
			setIsSearchFocused,
		},
	} = useController({
		items,
		value,
		onSelect,
		onClose,
		listenForOpenEvent,
		onEventOpen,
	});

	const selectedProviderItem = items?.find((item) => item.key === value?.key);
	const triggerLabel = isProviderActive
		? getProviderTriggerLabel(placeholder, selectedProviderItem ?? value)
		: placeholder;

	useEffect(() => {
		if (open) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "unset";
		}

		return () => {
			document.body.style.overflow = "unset";
		};
	}, [open]);

	return (
		<Translate>
			{({ translate }) => (
				<>
					{!hideTrigger && (
						<BettoProviderTriggerGroup>
							<BettoProviderTrigger
								type="button"
								data-testid={dataTestId}
								$active={isProviderActive}
								onClick={handleOpen}>
								<BettoProviderTriggerIcon
									src={providersIcon}
									alt=""
									aria-hidden
								/>
								<span>{triggerLabel}</span>
							</BettoProviderTrigger>
							{isProviderActive && (
								<BettoProviderTriggerClear
									type="button"
									aria-label="Clear provider"
									data-testid="button-casino-filter-providers-clear"
									$active={isProviderActive}
									onClick={handleClearSelection}>
									×
								</BettoProviderTriggerClear>
							)}
						</BettoProviderTriggerGroup>
					)}

					{open &&
						typeof document !== "undefined" &&
						createPortal(
							<BettoProviderRoot $open>
								<BettoProviderOverlay
									type="button"
									aria-label="Close providers"
									onClick={handleClose}
								/>
								<BettoProviderPanel onClick={(event) => event.stopPropagation()}>
									<BettoProviderHeader>
										<BettoProviderTitleGroup>
											<BettoProviderTitle data-testid="text-filter-modal-title">
												{placeholder}
											</BettoProviderTitle>
											<BettoProviderCount>{totalCount}</BettoProviderCount>
										</BettoProviderTitleGroup>
										<BettoProviderCloseButton
											type="button"
											data-testid="button-filter-modal-close"
											aria-label="Close"
											onClick={handleClose}>
											<AuthCloseIcon />
										</BettoProviderCloseButton>
									</BettoProviderHeader>

									<BettoProviderSearchSection>
										<BettoProviderSearchBar
											$focused={isSearchFocused || searchTerm.length > 0}>
											<BettoProviderSearchIcon
												src={searchIcon}
												alt=""
											/>
											<BettoProviderSearchInput
												ref={searchInputRef}
												type="text"
												data-testid="input-filter-modal-search"
												placeholder={translate(
													"betto.casino.searchProviders"
												)}
												value={searchTerm}
												onChange={(event) =>
													setSearchTerm(event.target.value)
												}
												onFocus={() => setIsSearchFocused(true)}
												onBlur={() => setIsSearchFocused(false)}
											/>
											{searchTerm.length > 0 && (
												<BettoProviderClearButton
													type="button"
													aria-label="Clear search"
													onClick={handleClearSearch}>
													×
												</BettoProviderClearButton>
											)}
										</BettoProviderSearchBar>

										{searchTerm.length > 0 &&
											(filteredItems.length === 0 ? (
												<BettoProviderInfo>
													{translate("casino.noSearchProvider")}
												</BettoProviderInfo>
											) : (
												<BettoProviderInfo>
													{translate("casino.searchProvider", {
														provider: filteredItems.length,
													})}
												</BettoProviderInfo>
											))}
									</BettoProviderSearchSection>

									<BettoProviderGrid className="item-wrapper">
										{filteredItems.map((item) => {
											const isSelected = item.key === selectedKey;
											const label = getProviderLabel(item);

											return (
												<BettoProviderCard
													key={item.key}
													type="button"
													$active={isSelected}
													data-active={isSelected ? "true" : "false"}
													data-testid={`button-filter-modal-item-${item.key}`}
													onClick={() => handleSelect(item)}>
													<BettoProviderLogo provider={item.key} />
													<BettoProviderCardFooter>
														<BettoProviderName title={label}>
															{label}
														</BettoProviderName>
														{item.count != null && (
															<BettoProviderGameCount>
																{item.count}
															</BettoProviderGameCount>
														)}
													</BettoProviderCardFooter>
												</BettoProviderCard>
											);
										})}
									</BettoProviderGrid>
								</BettoProviderPanel>
							</BettoProviderRoot>,
							document.body
						)}
				</>
			)}
		</Translate>
	);
};

export default BettoProviderModal;
